import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { NSOrderFillConstants } from '@ruby/configs/order-fill.constants';
import { IOrderFillDetail, IOrderFillPie, IOrderFillRequest, IOrderFillViewDateRange } from '@ruby/modules/order-fill/model/order-fill.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import {
  EOrderBarChartTitle,
  EOrderFillDateInterval,
  EOrderFillEnums,
  EOrderPieChartTitle,
  EOrderViewType
} from '@ruby/shared/enums/order-fill.enums';

@Component({
  selector: 'ng-order-fill',
  templateUrl: 'order-fill.component.html',
  styleUrls: ['./order-fill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrderFillComponent implements OnChanges {
  @Input() loading = false;
  @Input() loadingFilters = false;
  @Input() requested!: number;
  @Input() accepted!: number;
  @Input() filled!: number;
  @Input() acceptedNotFilled!: number;
  @Input() notAccepted!: number;
  @Input() metrics: Array<IOrderFillDetail> = [];
  @Input() dateInterval: { previous?: EOrderFillDateInterval; current?: EOrderFillDateInterval } = { previous: undefined, current: undefined };
  @Input() doofFeature = false;
  @Output() selectedOrderFill = new EventEmitter<IOrderFillRequest>();
  chartConfig!: Record<string, unknown>;
  orderFillConfig!: Record<string, unknown>;
  acceptedFillConfig!: Record<string, unknown>;
  updatedConfig: Record<string, unknown> = {};
  summary: Array<IOrderFillPie> = [];
  orderFillTypes = EOrderFillEnums;
  viewType: EOrderViewType = EOrderViewType.WEEKLY;
  barChartTitle = EOrderBarChartTitle.WEEKLY;
  orderViewTypeOption = EOrderViewType;
  customDateRange!: IOrderFillViewDateRange;
  pieChartTitle = EOrderPieChartTitle.DAILY;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.requested || changes.accepted || changes.filled) {
      this.calculateSummary();
      this.chartConfig = this.setUpChartConfig();
      this.updatedConfig = this.chartConfig;
      this.orderFillConfig = this.setUpOrderAcceptedFill(this.requested);
      this.acceptedFillConfig = this.setUpOrderAcceptedFill(this.accepted);
    }
  }

  /**
   * Emit Order Fill
   *
   * @summary Emit selected order fill request
   * @param $request: IOrderFillRequest
   * @returns void
   */
  emitOrderFill($request: IOrderFillRequest): void {
    this.viewType = $request.viewType;
    this.barChartTitle = EOrderBarChartTitle[this.viewType];
    this.pieChartTitle = EOrderPieChartTitle[this.viewType];
    this.selectedOrderFill.emit($request);
  }

  /**
   * Return Navigation
   *
   * @summary Returns to previous chart navigation state
   * @returns void
   */
  returnNavigation(): void {
    this.customDateRange = {
      ...this.customDateRange,
      range: this.customDateRange.range,
      dateInterval: {
        previous: this.dateInterval.current!,
        current: this.dateInterval.previous!
      }
    };
  }

  /**
   * Update Config
   *
   * @summary Updates chart configuration based on indicator selection
   * @param $type?: EOrderFillEnums
   * @returns void
   */
  updateConfig($type?: EOrderFillEnums): void {
    const selected: IOrderFillPie | undefined = this.summary.find((item: IOrderFillPie) => item.name === $type);

    const updateChartData = (item: IOrderFillPie) => {
      item.itemStyle = {
        borderColor: '#000',
        borderWidth: 2
      };
    };

    if (selected) {
      switch (selected.name) {
        case EOrderFillEnums.FILLED:
          updateChartData(selected);
          break;
        case EOrderFillEnums.ACCEPTED_NOT_FILLED:
          updateChartData(selected);
          const filled = this.summary.find((item: IOrderFillPie) => item.name === EOrderFillEnums.FILLED);
          if (filled) {
            updateChartData(filled);
          }
          break;
        case EOrderFillEnums.NOT_ACCEPTED:
          this.summary.forEach((item: IOrderFillPie) => updateChartData(item));
      }
    } else {
      this.calculateSummary();
    }

    this.updatedConfig = this.setUpChartConfig();
  }

  private calculateSummary(): void {
    this.summary = [
      { name: EOrderFillEnums.FILLED, value: this.filled },
      { name: EOrderFillEnums.ACCEPTED_NOT_FILLED, value: this.acceptedNotFilled },
      { name: EOrderFillEnums.NOT_ACCEPTED, value: this.notAccepted }
    ];
  }

  private setUpChartConfig(): Record<string, unknown> {
    const colors: Array<string> = [...NSOrderFillConstants.datePieConfigs.series[0].color as Array<string>];
    const seriesConfig: Record<string, unknown> = (this.filled === 0 && this.accepted === 0 && this.requested === 0)
      ? NSOrderFillConstants.datePieNoConfigs.series[0]
      : NSOrderFillConstants.datePieConfigs.series[0];

    return {
      tooltip: NSOrderFillConstants.datePieConfigs.tooltip,
      legend: NSOrderFillConstants.datePieConfigs.legend,
      series: [
        {
          ...seriesConfig,
          color: colors,
          data: this.summary
        }
      ]
    };
  }

  private setUpOrderAcceptedFill(value: number): Record<string, unknown> {
    let colors: Array<string> = [...NSOrderFillConstants.orderFillPieConfigs.series[0].color];
    const filled: number = Math.round((this.filled / value) * 100);
    let filterItems: Array<IOrderFillPie> = [{ name: '0%', value: 0 }];

    if (value > 0 && this.filled > 0) {
      if ((this.filled / value) < 0.5) {
        filterItems = [
          { name: `0%`, value: this.filled / value },
          { name: `${ filled.toString() }%`, value: 1 - (this.filled / value) }
        ];
      } else {
        filterItems = [
          { name: `${ filled.toString() }%`, value: this.filled / value },
          { name: `0%`, value: 1 - (this.filled / value) }
        ];
      }

      if (this.filled === value) {
        filterItems.splice(1, 1);
      }
    } else {
      colors = [EColors.SUPER_LIGHT];
    }

    return {
      tooltip: NSOrderFillConstants.orderFillPieConfigs.tooltip,
      legend: NSOrderFillConstants.orderFillPieConfigs.legend,
      series: [
        {
          ...NSOrderFillConstants.orderFillPieConfigs.series[0],
          color: colors,
          data: filterItems
        }
      ]
    };
  }
}
